import {
    useSyncExternalStore,
    type ReactNode,
    useContext,
    useEffect,
    Fragment,
    type FC
} from "react";
import {
    Text as NativeText,
    Animated,
    View
} from "react-native";
import type ICommandListProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePresence
} from "../../../../helpers/motion";
import type {
    CommandActiveStoreType,
    CommandItemModelType,
    CommandActiveState,
    CommandContextType,
    CommandBlockType
} from "../../type";
import {
    ChevronRight as ChevronRightIcon,
    ExternalLink as ExternalLinkIcon
} from "lucide-react-native";
import {
    getMirrorStyle,
    isMirroredIcon
} from "../../../../helpers/direction";
import {
    getCommandShortcutLabel,
    getCommandShortcutAria
} from "../../util";
import {
    warnOnce
} from "../../../../utils/development";
import {
    COMMAND_LIST_MAX_ROWS
} from "../../stylesheet";
import {
    getKbdPlatform
} from "../../../kbd/util";
import {
    CommandContext
} from "../../context";
import PopoverSurfaceSeparator from "../../../popoverSurface/components/separator";
import PopoverSurfaceHeading from "../../../popoverSurface/components/heading";
import PopoverSurfaceStatus from "../../../popoverSurface/components/status";
import PopoverSurfaceEmpty from "../../../popoverSurface/components/empty";
import PopoverSurfaceList from "../../../popoverSurface/components/list";
import OptionRow from "../../../optionRow";
import Text from "../../../text";

const CommandListRow: FC<{
    renderRow: (item: CommandItemModelType, activeState: CommandActiveState) => ReactNode;
    store: CommandActiveStoreType;
    item: CommandItemModelType;
    isSheet: boolean;
}> = ({
    renderRow,
    isSheet,
    store,
    item
}) => {
    const getActiveState = (): CommandActiveState => isSheet ? 0 : store.getItemState(item.key);

    const activeState = useSyncExternalStore(store.subscribe, getActiveState, getActiveState);

    return renderRow(item, activeState);
};

const CommandListContent: FC<ICommandListProps & {
    context: CommandContextType;
}> = ({
    context,
    style
}) => {
    const {
        customLocalize,
        loadingProps,
        customTheme,
        emptyProps,
        isLoading,
        isEmpty,
        variant,
        blocks,
        listId,
        query
    } = context;

    const {
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const platform = getKbdPlatform();

    const isSheet = variant === "sheet";

    const {
        progress: emptyProgress
    } = usePresence({
        enterIntent: "surfaceEnter",
        exitIntent: "surfaceExit",
        isVisible: isEmpty,
        customTheme
    });

    const {
        divider: dividerDynamicStyle,
        parent: parentDynamicStyle,
        hidden: hiddenDynamicStyle
    } = useStyles({
        borders,
        colors,
        spaces
    });

    const mirrorStyle = getMirrorStyle({
        isMirrored: isMirroredIcon({
            name: "ChevronRight"
        }),
        direction
    });

    const parentSeparator = direction === "rtl" ? "‹" : "›";

    const renderLabel = (item: CommandItemModelType) => {
        const {
            parentTitle,
            title
        } = item.props;

        const match = item.match;

        const isHighlighted = match !== undefined && match.end > match.start;

        const parent = parentTitle ? <NativeText
            style={parentDynamicStyle}
        >
            {`${parentTitle} ${parentSeparator} `}
        </NativeText> : null;

        if(!match || !isHighlighted) {
            return <Fragment>
                {parent}
                {title}
            </Fragment>;
        }

        return <Fragment>
            {parent}
            {title.slice(0, match.start)}
            <OptionRow.Highlight
                customTheme={customTheme}
            >
                {title.slice(match.start, match.end)}
            </OptionRow.Highlight>
            {title.slice(match.end)}
        </Fragment>;
    };

    const renderTrailing = (item: CommandItemModelType) => {
        const {
            isExternal,
            isSelected,
            pageKey
        } = item.props;

        if(!isExternal && !isSelected && !pageKey) {
            return undefined;
        }

        return <Fragment>
            {
                isSelected ?
                    <View
                        style={[
                            stylesheet.hidden,
                            hiddenDynamicStyle
                        ]}
                    >
                        <Text
                            customTheme={customTheme}
                        >
                            {localize("command-current")}
                        </Text>
                    </View>
                :
                    null
            }
            {
                isExternal ?
                    <ExternalLinkIcon
                        style={getMirrorStyle({
                            isMirrored: isMirroredIcon({
                                name: "ExternalLink"
                            }),
                            direction
                        })}
                        color={colors.content.icon.low}
                        size={iconSizes.small}
                        aria-hidden={true}
                    />
                :
                    null
            }
            {
                pageKey ?
                    <ChevronRightIcon
                        color={colors.content.icon.mid}
                        size={iconSizes.small}
                        style={mirrorStyle}
                        aria-hidden={true}
                    />
                :
                    null
            }
        </Fragment>;
    };

    const renderRow = (item: CommandItemModelType, activeState: CommandActiveState): ReactNode => {
        const {
            props
        } = item;

        const isActive = activeState !== 0;

        const shortcutProps = props.shortcut ? {
            "aria-keyshortcuts": getCommandShortcutAria({
                shortcut: props.shortcut,
                platform
            })
        } : {};

        return <OptionRow
            {...shortcutProps}
            shortcut={props.shortcut ? getCommandShortcutLabel({
                shortcut: props.shortcut,
                platform
            }) : undefined}
            onHoverIn={() => context.onItemHover(item.key)}
            variant={isSheet ? "sheet" : "popover"}
            isShowCheck={props.isSelected === true}
            onPress={() => context.onItemPress({
                isNewTab: false,
                key: item.key
            })}
            trailing={renderTrailing(item)}
            description={props.description}
            isSelected={props.isSelected}
            isDisabled={item.isDisabled}
            customTheme={customTheme}
            label={renderLabel(item)}
            isShowActiveRing={false}
            aria-selected={isActive}
            leading={props.leading}
            isActive={isActive}
            type={props.type}
            meta={props.meta}
            icon={props.icon}
            key={item.key}
            role="option"
            id={item.id}
        />;
    };

    const renderItem = (item: CommandItemModelType): ReactNode => <CommandListRow
        store={context.store}
        renderRow={renderRow}
        isSheet={isSheet}
        key={item.key}
        item={item}
    />;

    const renderItems = (block: CommandBlockType) => {
        return block.items.map((item, index) => <Fragment
            key={item.key}
        >
            {
                isSheet && index > 0 ?
                    <View
                        style={dividerDynamicStyle}
                        aria-hidden={true}
                    />
                :
                    null
            }
            {renderItem(item)}
        </Fragment>);
    };

    const renderBlock = (block: CommandBlockType) => {
        if(block.isSeparator) {
            return <PopoverSurfaceSeparator
                customTheme={customTheme}
                key={block.key}
            />;
        }

        if(!block.title) {
            return <Fragment
                key={block.key}
            >
                {renderItems(block)}
            </Fragment>;
        }

        const headingId = `${block.id}-heading`;

        return <View
            aria-labelledby={headingId}
            style={stylesheet.group}
            key={block.key}
            role="group"
        >
            <PopoverSurfaceHeading
                variant={isSheet ? "sheet" : "popover"}
                customTheme={customTheme}
                id={headingId}
            >
                {block.title}
            </PopoverSurfaceHeading>
            {renderItems(block)}
        </View>;
    };

    const renderEmpty = () => {
        if(!isEmpty) {
            return null;
        }

        return <Animated.View
            style={{
                opacity: emptyProgress
            }}
        >
            <PopoverSurfaceEmpty
                description={emptyProps?.description}
                customLocalize={customLocalize}
                title={emptyProps?.title}
                customTheme={customTheme}
                searchText={query}
            />
            {
                blocks.length ?
                    <PopoverSurfaceSeparator
                        customTheme={customTheme}
                    />
                :
                    null
            }
        </Animated.View>;
    };

    const renderLoading = () => {
        if(!isLoading) {
            return null;
        }

        return <PopoverSurfaceStatus
            customLocalize={customLocalize}
            customTheme={customTheme}
        >
            {loadingProps?.title}
        </PopoverSurfaceStatus>;
    };

    return <View
        style={stylesheet.wrapper}
    >
        <PopoverSurfaceList
            aria-label={localize("command-commands")}
            maxRows={COMMAND_LIST_MAX_ROWS[variant]}
            customTheme={customTheme}
            isBusy={isLoading}
            style={style}
            id={listId}
        >
            {renderEmpty()}
            {blocks.map(renderBlock)}
            {renderLoading()}
        </PopoverSurfaceList>
    </View>;
};

const CommandList: FC<ICommandListProps> = (props) => {
    const context = useContext(CommandContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: "Command.List: must be rendered inside a Command."
        });
    }, [
        context
    ]);

    if(!context) {
        return null;
    }

    return <CommandListContent
        {...props}
        context={context}
    />;
};
export default Object.assign(CommandList, {
    commandPart: "list"
});
